import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";

import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import PanelMenu, { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowLeftIcon from "@tCentro/Icons/ArrowLeftIcon";
import ArrowRightIcon from "@tCentro/Icons/ArrowRightIcon";
import MinusIcon from "@tCentro/Icons/MinusIcon";
import PlusIcon from "@tCentro/Icons/PlusIcon";
import SearchIcon from "@tCentro/Icons/SearchIcon";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    // Accordian Section Styles
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    input?: TextFieldPresentationProps;
    accordionMainContainerStyles?: InjectableCss;

    // Drawer Section Styles
    panelMenuStyles?: PanelMenuPresentationProps;
    panelrowStyles?: PanelRowPresentationProps;
    childrenStyles?: InjectableCss;
    checkboxStyles?: CheckboxPresentationProps;
    drawerStyles?: DrawerPresentationProps;
    childrenPanelrowStyles?: PanelRowPresentationProps;
    panelContentStyles?: InjectableCss;
    parentBtnStyles: InjectableCss;
    clearAllBtnStyles: InjectableCss;
    applyBtnStyles?: InjectableCss;
    attributeTypeNameStyles?: InjectableCss;
    showResultsStyles?: InjectableCss;
    searchinputStyles?: TextFieldPresentationProps;
    drawerMainContainerStyles?: InjectableCss;
    seeLessLinkStylesMobile?: InjectableCss;
    seeAllLinkStylesMobile?: InjectableCss;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    // Accordian Section Styles
    accordionMainContainerStyles: {
        css: css`
            display: block;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    accordionSection: {
        toggleIconProps: {
            src: MinusIcon,
        },

        expandIconProps: {
            src: PlusIcon,
        },
        headerProps: {
            css: css`
                button[aria-expanded="true"] {
                    border-bottom: none !important;
                    padding-top: 20px !important;
                    padding-bottom: 5px !important;
                }
                button[aria-expanded="false"] {
                    padding: 20px 9px;
                    padding-left: 15px;
                }
                > button {
                    border: none !important;
                    border-bottom: 1px solid ${COLORS.disabled.dark} !important;
                    background: none !important;
                    &:focus {
                        padding: 20px 9px;
                        padding-left: 15px;
                        padding-top: 20px !important;
                    }
                    span {
                        color: ${getProp("theme.typography.p.color")} !important;
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                        font-size: ${TYPOGRAPHY.size.base} !important;
                        svg {
                            height: 19.5px;
                            width: 19.5px;
                        }
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
                border: none;
                border-bottom: 1px solid ${COLORS.disabled.dark} !important;
                > div {
                    padding-bottom: 15px;
                }
            `,
        },
    },
    input: {
        iconProps: { src: SearchIcon },
        cssOverrides: {
            formField: css`
                width: 100%;
                div {
                    input {
                        border-radius: 40px !important;
                        padding: 10px 16px !important;
                        padding-right: 32px !important;
                        height: 32px;
                        background-color: ${COLORS.background.grayShade} !important;
                        color: ${COLORS.black} !important;
                        font-size: ${TYPOGRAPHY.size.smaller} !important;
                        font-weight: ${TYPOGRAPHY.weight.base};
                    }
                    span {
                        top: 10px !important;
                        right: 12px !important;
                        width: 12px !important;
                        height: 12px !important;
                        padding: 0px !important;
                        svg {
                            width: 12px;
                            height: 12px;
                        }
                    }
                }
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            display: grid !important;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            display: flex;
            justify-content: center;
            align-items: flex-start;
            flex-direction: row-reverse;
            span {
                border: 2px solid #9e9d9e !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                &:focus {
                    box-shadow: none !important;
                }
            }
            label {
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: 20px;
                color: ${getProp("theme.typography.p.color")};
                width: 100%;
                margin: 0 !important;
                > div {
                    padding-right: 12px;
                    div {
                        &:first-child {
                            flex-basis: 92% !important;
                        }
                        &:last-child {
                            display: flex;
                            justify-content: flex-end;
                            flex-basis: 53% !important;
                            white-space: nowrap;
                        }
                    }
                }
            }
            span[role="checkbox"] {
                height: 24px;
                width: 24px;
                border-radius: 2px !important;
                min-width: 24px !important;
                border: 2px solid #262525 !important;
                svg {
                    height: 24px;
                    width: 24px;
                }
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-word;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            display: flex;
            justify-content: center;
            align-items: flex-start;
            flex-direction: row-reverse;
            span {
                border: 2px solid ${COLORS.black} !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                &:focus {
                    box-shadow: none !important;
                }
            }
            label {
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: 20px;
                color: ${getProp("theme.typography.p.color")};
                width: 100%;
                margin: 0 !important;
                word-break: break-word;
                > div {
                    padding-right: 12px;
                    div {
                        &:first-child {
                            flex-basis: 92% !important;
                        }
                        &:last-child {
                            display: flex;
                            justify-content: flex-end;
                            flex-basis: 53% !important;
                            white-space: nowrap;
                        }
                    }
                }
            }
            span[role="checkbox"] {
                height: 24px;
                width: 24px;
                min-width: auto !important;
                border-radius: 2px !important;
                background: none !important;
                color: ${COLORS.black} !important;
                svg {
                    height: 24px;
                    width: 24px;
                }
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-word; ;
            `,
            // weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },

    // Drawer Section Styles
    seeLessLinkStylesMobile: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 19px;
                        padding-top: 8px;
                    `,
                    css`
                        margin-left: 19px;
                        padding-top: 8px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    seeAllLinkStylesMobile: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 19px;
                        padding-top: 8px;
                    `,
                    css`
                        margin-left: 19px;
                        padding-top: 8px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    drawerMainContainerStyles: {
        css: css`
            display: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    panelMenuStyles: {
        closeIconProps: {
            src: "X",
            css: css`
                margin-left: 50px;
            `,
        },

        headerColor: `#FFFFFF`,
        bodyColor: `#FFFFFF`,
        backIconProps: {
            src: ArrowLeftIcon,
            css: css`
                margin-right: 50px;
                width: 18px;
                height: 11.25px;
                svg {
                    width: 18px;
                    height: 11.25px;
                }
            `,
        },
    },
    panelrowStyles: {
        css: css`
            background: ${COLORS.white} !important;
            padding: 0px;
        `,
    },
    drawerStyles: {
        size: 350,
        cssOverrides: {
            drawerBody: css`
                background: ${COLORS.white} !important;
            `,
            drawerContent: css`
                nav {
                    border-bottom: 1px solid #bcc3cd;
                    padding: 20px 18px 20px 16px;
                }
            `,
            drawerTitle: css`
                border-bottom: 1px solid #bcc3cd;
            `,
            headlineTypography: css`
                font-size: 24px !important;
                font-weight: 600 !important;
                line-height: 32px !important;
                margin-left: 16px !important;
                margin-top: 15px !important;
            `,
        },
    },
    childrenStyles: {
        css: css`
            display: flex;
            margin-top: 32px;
            align-items: center;
        `,
    },
    checkboxStyles: {
        css: css`
            margin-right: 12px;
            > span[role="checkbox"] {
                border: 1px solid #262525;
                min-width: auto !important;
                border-radius: 2px !important;
                background: none !important;
                color: #262525 !important;
            }
        `,
        iconProps: {
            size: 24,
        },
        color: `#262525;`,
    },
    childrenPanelrowStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            background: ${COLORS.white};
            padding: 0px 10px 0px 20px;
        `,
    },
    panelContentStyles: {
        css: css`
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            color: #000000;
        `,
    },
    clearAllBtnStyles: {
        css: css`
            background: #636161;
            opacity: 0.5;
            border-radius: 24px;
            border: none;
            > span {
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
            }
        `,
    },
    applyBtnStyles: {
        css: css`
            border-radius: 24px;
        `,
    },
    parentBtnStyles: {
        css: css`
            border-top: 1px solid #bcc3cd !important;
            display: flex;
            justify-content: space-around;
            padding-top: 20px;
        `,
    },
    searchinputStyles: {
        iconProps: { src: SearchIcon },
        cssOverrides: {
            formField: css`
                margin-top: 16px !important;
                width: 90%;
                display: block;
                margin: auto;
            `,
            inputSelect: css`
                background: #f2f2f2;
                border-radius: 40px;
            `,
        },
    },
    showResultsStyles: {
        css: css`
            display: block;
            height: 32px;
            margin: 20px auto;
            border-radius: 24px;
        `,
    },
    attributeTypeNameStyles: {
        css: css`
            position: absolute;
            top: 7px;
            left: 58px;
            z-index: 1;
            color: #000000;
            font-size: 18px;
            font-weight: 600;
            line-height: 32px;
        `,
    },
};

interface OwnProps extends WidgetProps {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const attributeTypeFacets = getProductListDataViewProperty(state, "attributeTypeFacets");
    const pagination = getProductListDataViewProperty(state, "pagination");
    const attributeFilteredData = state.t_components?.attributeNames?.mainData;

    return {
        attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
        pageBrandId: state.pages.productList.productFilters.pageBrandId,
        loaded: !!getProductListDataView(state).value,
        productFilters: state.pages.productList.productFilters,
        totalItemCount: pagination?.totalItemCount,
        attributeFilteredData,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    attributeTypeFacets,
    attributeFilteredData,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;
    const [filter, setFilter] = useState("");

    if (attributeFilteredData?.length === 0) {
        return null;
    }

    const filterDrawerCloseHandler = () => {
        const btnElem = document.getElementById("show-filtered-results");
        if (btnElem) {
            btnElem.click();
        }
    };

    return (
        <>
            <StyledWrapper {...styles.accordionMainContainerStyles}>
                <ManagedAccordionSection
                    title={title}
                    {...styles.accordionSection}
                    initialExpanded={expandByDefault || anySelected}
                >
                    <TextField
                        {...styles.input}
                        id="userName"
                        onChange={event => setFilter(event.target.value)}
                        value={filter}
                        placeholder={translate("Search")}
                    />

                    {limitedFacets
                        ?.filter(facet => facet.name?.toLowerCase().includes(filter.toLowerCase()) || filter === "")
                        .map(facet => (
                            <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                                <Checkbox
                                    {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                    checked={facet.selected}
                                    onChange={() => onChangeFacet(facet)}
                                    data-test-selector={`facetFilter${facet.id}`}
                                >
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                            flex-direction: row;
                                        `}
                                    >
                                        <StyledWrapper
                                            css={css`
                                                flex-basis: 90%;
                                            `}
                                        >
                                            {facet.name}
                                        </StyledWrapper>
                                        <StyledWrapper
                                            css={css`
                                                color: ${COLORS.background.tag};
                                            `}
                                        >
                                            {facet.count !== -1 && `(${facet.count.toLocaleString("en-US")})`}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </Checkbox>
                            </CheckboxGroup>
                        ))}

                    {facets &&
                        facets.length > showMoreLimit &&
                        (expanded ? (
                            <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                                {translate("See Less")}
                            </Link>
                        ) : (
                            <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                                {translate("See All")}
                            </Link>
                        ))}
                </ManagedAccordionSection>
            </StyledWrapper>
            <StyledWrapper {...styles.drawerMainContainerStyles}>
                <PanelMenu
                    {...styles.panelMenuStyles}
                    layer={0}
                    closeOverlay={filterDrawerCloseHandler}
                    panelTrigger={
                        <PanelRow {...styles.panelrowStyles}>
                            <Typography {...styles.panelContentStyles}>{title}</Typography>
                            <ArrowRightIcon />
                        </PanelRow>
                    }
                >
                    <Typography {...styles.attributeTypeNameStyles}>{title}</Typography>
                    <TextField
                        {...styles.searchinputStyles}
                        id="userName"
                        onChange={event => setFilter(event.target.value)}
                        value={filter}
                        placeholder={translate(`Search ${title}`)}
                    />
                    {limitedFacets
                        ?.filter(facet => facet.name?.toLowerCase().includes(filter.toLowerCase()) || filter === "")
                        .map((facet, index) => (
                            <PanelRow {...styles.childrenPanelrowStyles} key={index}>
                                <Typography {...styles.childrenStyles}>
                                    <Checkbox
                                        {...styles.checkboxStyles}
                                        key={facet.id}
                                        checked={facet.selected}
                                        onChange={() => onChangeFacet(facet)}
                                        data-test-selector={`facetFilter${facet.id}`}
                                    />

                                    <Typography as="span" {...styles.panelContentStyles}>
                                        {facet.name} ({facet.count.toLocaleString("en-US")})
                                    </Typography>
                                </Typography>
                            </PanelRow>
                        ))}
                    {facets &&
                        facets.length > showMoreLimit &&
                        (expanded ? (
                            <Link {...styles.seeLessLinkStylesMobile} onClick={() => setExpanded(false)}>
                                {translate("See Less")}
                            </Link>
                        ) : (
                            <Link {...styles.seeAllLinkStylesMobile} onClick={() => setExpanded(true)}>
                                {translate("See All")}
                            </Link>
                        ))}
                </PanelMenu>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductListFiltersAccordionSection);
